import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { useAdminApi } from '@/hooks/useAdminApi';
import { useOrganizationList } from '@clerk/clerk-react';
import { Plus, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { UserFilters, UserStats, UsersTable } from './admin-users/components/UserOverview';
import type { ApiUser, UserProfile } from './admin-users/model';

import { UserDetailsDialog } from './admin-users/components/UserDetailsDialog';
import {
  AddProfileDialog,
  BulkUploadDialog,
  DeleteUserDialog,
  InviteUserDialog,
} from './admin-users/components/WorkflowDialogs';
export default function AdminUsers() {
  const adminApi = useAdminApi();
  const adminApiRef = useRef(adminApi);
  adminApiRef.current = adminApi;
  const [searchParams, setSearchParams] = useSearchParams();
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState(searchParams.get('role') || '');
  const [orgFilter, setOrgFilter] = useState('');
  const [gymFilter, setGymFilter] = useState('');
  const [sortBy, setSortBy] = useState<'created_at' | 'name'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);

  // Clerk organization data
  const { userMemberships, isLoaded: orgsLoaded } = useOrganizationList({
    userMemberships: { infinite: true },
  });
  const [orgMembers, setOrgMembers] = useState<Map<string, string[]>>(new Map());
  const [loadingOrgMembers, setLoadingOrgMembers] = useState(false);

  // Get list of organizations the user has access to
  const organizations = useMemo(() => {
    if (!orgsLoaded || !userMemberships.data) return [];
    return userMemberships.data.map((m) => ({
      id: m.organization.id,
      name: m.organization.name,
    }));
  }, [orgsLoaded, userMemberships.data]);

  // Fetch all org members on mount (for displaying org column in table)
  useEffect(() => {
    if (!orgsLoaded || !userMemberships.data) return;

    const fetchAllOrgMembers = async () => {
      setLoadingOrgMembers(true);
      try {
        const newOrgMembers = new Map<string, string[]>();

        for (const membership of userMemberships.data) {
          const orgId = membership.organization.id;
          const memberships = await membership.organization.getMemberships();
          const memberUserIds = memberships.data
            ?.map((m) => m.publicUserData?.userId)
            .filter((id): id is string => Boolean(id));

          newOrgMembers.set(orgId, memberUserIds || []);
        }

        setOrgMembers(newOrgMembers);
      } catch (err) {
        console.error('Error fetching org members:', err);
      } finally {
        setLoadingOrgMembers(false);
      }
    };

    fetchAllOrgMembers();
  }, [orgsLoaded, userMemberships.data]);

  // Create reverse lookup: user ID → org names
  const userOrgMap = useMemo(() => {
    const map = new Map<string, string[]>();

    for (const [orgId, userIds] of orgMembers) {
      const org = organizations.find((o) => o.id === orgId);
      if (!org) continue;

      for (const userId of userIds) {
        const existing = map.get(userId) || [];
        if (!existing.includes(org.name)) {
          map.set(userId, [...existing, org.name]);
        }
      }
    }

    return map;
  }, [orgMembers, organizations]);
  // Store total counts from API (independent of filter)
  const [totalCounts, setTotalCounts] = useState({
    total: 0,
    admins: 0,
    agents: 0,
    affiliates: 0,
    members: 0,
    individuals: 0,
  });

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<ApiUser | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviteRole, setInviteRole] = useState('member');
  const [inviteAffiliateId, setInviteAffiliateId] = useState('');
  const [inviteAffiliateName, setInviteAffiliateName] = useState('');
  const [affiliates, setAffiliates] = useState<Array<{ id: string; name: string }>>([]);
  const [isInviting, setIsInviting] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  // Bulk upload state
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkEmails, setBulkEmails] = useState('');
  const [bulkRole, setBulkRole] = useState('member');
  const [bulkAffiliateId, setBulkAffiliateId] = useState('');
  const [isBulkUploading, setIsBulkUploading] = useState(false);
  const [bulkResults, setBulkResults] = useState<{
    results: Array<{ email: string; status: 'created' | 'skipped'; reason?: string }>;
    summary: { total: number; created: number; skipped: number };
  } | null>(null);
  const [resendingInviteFor, setResendingInviteFor] = useState<string | null>(null);
  const [requestingPasswordResetFor, setRequestingPasswordResetFor] = useState<string | null>(null);
  // Profile management state (HQ-012)
  const [userProfiles, setUserProfiles] = useState<UserProfile[]>([]);
  const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
  const [showAddProfileModal, setShowAddProfileModal] = useState(false);
  const [addProfileType, setAddProfileType] = useState('');
  const [addProfileTerritoryName, setAddProfileTerritoryName] = useState('');
  const [addProfileAffiliateId, setAddProfileAffiliateId] = useState('');
  const [addProfileMemberRole, setAddProfileMemberRole] = useState<'owner' | 'coach' | 'member'>(
    'member'
  );
  const [isAddingProfile, setIsAddingProfile] = useState(false);
  const [hqAdminConfirmText, setHqAdminConfirmText] = useState('');
  const [removingProfileId, setRemovingProfileId] = useState<string | null>(null);
  const [settingPrimaryId, setSettingPrimaryId] = useState<string | null>(null);
  // HQ-001: Enriched user detail state
  const [userPhone, setUserPhone] = useState<string | null>(null);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneInput, setPhoneInput] = useState('');
  const [isSavingPhone, setIsSavingPhone] = useState(false);
  const [agentDetails, setAgentDetails] = useState<{
    id: string;
    commission_rate_equipment: number | null;
    commission_rate_subs: number | null;
    territory_name: string | null;
  } | null>(null);
  const [editingCommission, setEditingCommission] = useState(false);
  const [commissionEquipInput, setCommissionEquipInput] = useState('');
  const [commissionSubsInput, setCommissionSubsInput] = useState('');
  const [isSavingCommission, setIsSavingCommission] = useState(false);
  const [paymentDetails, setPaymentDetails] = useState<{
    stripe_customer_id: string | null;
    subscription_status: string | null;
  } | null>(null);

  // Update URL when role filter changes
  const handleRoleFilterChange = (value: string) => {
    setRoleFilter(value);
    if (value) {
      setSearchParams({ role: value });
    } else {
      setSearchParams({});
    }
  };

  const fetchUsers = useCallback(
    async (cursor?: string) => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await adminApiRef.current.getUsers({
          role: roleFilter || undefined,
          search: searchQuery || undefined,
          affiliate_id: gymFilter || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
          cursor,
          limit: 50,
        });

        const data = response.data;
        if (response.success && data) {
          if (cursor) {
            setUsers((prev) => [...prev, ...data.users]);
          } else {
            setUsers(data.users);
          }
          setHasMore(data.pagination.has_more);
          setNextCursor(data.pagination.next_cursor);
          // Update total counts from API (these are always the full totals)
          if (data.counts) {
            setTotalCounts({
              total: data.counts.total || 0,
              admins: data.counts.admins || 0,
              agents: data.counts.agents || 0,
              affiliates: data.counts.affiliates || 0,
              members: data.counts.members || 0,
              individuals: data.counts.individuals || 0,
            });
          }
        } else {
          // Show actual error from API
          setError(response.error || 'Failed to load users');
        }
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(err instanceof Error ? err.message : 'Failed to load users');
      } finally {
        setIsLoading(false);
      }
    },
    [roleFilter, searchQuery, gymFilter, sortBy, sortOrder]
  );

  // Fetch users on mount and when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers();
    }, 300); // Debounce search

    return () => clearTimeout(timer);
  }, [fetchUsers]);

  // Fetch affiliates for gym assignment dropdown
  useEffect(() => {
    const fetchAffiliates = async () => {
      try {
        const response = await adminApiRef.current.getAffiliates({ limit: 100 });
        if (response.success && response.data?.affiliates) {
          setAffiliates(
            response.data.affiliates.map((a: { id: string; name: string }) => ({
              id: a.id,
              name: a.name,
            }))
          );
        }
      } catch (error) {
        console.error('Failed to fetch affiliates:', error);
      }
    };
    fetchAffiliates();
  }, []);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleViewUser = (user: ApiUser) => {
    handleViewUserWithProfiles(user);
  };

  const handleAddUser = () => {
    setInviteEmail('');
    setInviteName('');
    setInviteRole('member');
    setInviteAffiliateId('');
    setInviteAffiliateName('');
    setShowAddModal(true);
  };

  const handleInviteUser = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Email is required');
      return;
    }

    // Validate role-specific required fields
    if (inviteRole === 'affiliate_owner' && !inviteAffiliateId && !inviteAffiliateName.trim()) {
      toast.error('Please select an existing gym or enter a new gym name');
      return;
    }
    if (inviteRole === 'affiliate_coach' && !inviteAffiliateId) {
      toast.error('Please select a gym to assign this coach to');
      return;
    }

    setIsInviting(true);
    try {
      const response = await adminApiRef.current.inviteUser({
        email: inviteEmail.trim(),
        name: inviteName.trim() || undefined,
        role: inviteRole,
        affiliate_id: inviteAffiliateId || undefined,
        affiliate_name: inviteRole === 'affiliate_owner' ? inviteAffiliateName.trim() : undefined,
      });
      if (response.success) {
        toast.success(`Invitation sent to ${inviteEmail}`);
        setShowAddModal(false);
        setInviteEmail('');
        setInviteName('');
        setInviteRole('member');
        setInviteAffiliateId('');
        setInviteAffiliateName('');
        // Refresh the user list to show the new invited user
        fetchUsers();
      } else {
        toast.error(response.error || 'Failed to send invitation');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send invitation';
      toast.error(message);
    } finally {
      setIsInviting(false);
    }
  };

  // Profile management handlers (HQ-012)
  const loadUserProfiles = useCallback(async (userId: string) => {
    setIsLoadingProfiles(true);
    try {
      const response = await adminApiRef.current.getUserDetail(userId);
      if (response.success && response.data) {
        setUserProfiles(response.data.profiles || []);
        setUserPhone(response.data.user.phone ?? null);
        setAgentDetails(response.data.agent_details ?? null);
        setPaymentDetails(response.data.payment_details ?? null);
      }
    } catch (err) {
      console.error('Failed to load user profiles:', err);
      toast.error('Failed to load user profiles');
    } finally {
      setIsLoadingProfiles(false);
    }
  }, []);

  const handleViewUserWithProfiles = useCallback(
    (user: ApiUser) => {
      setSelectedUser(user);
      setShowViewModal(true);
      setUserProfiles([]);
      loadUserProfiles(user.id);
    },
    [loadUserProfiles]
  );

  // HQ-001: Save phone number
  const handleSavePhone = async () => {
    if (!selectedUser) return;
    setIsSavingPhone(true);
    try {
      const response = await adminApiRef.current.updateUser(selectedUser.id, {
        phone: phoneInput || undefined,
      });
      if (response.success) {
        setUserPhone(phoneInput || null);
        setEditingPhone(false);
        toast.success('Phone number updated');
      } else {
        toast.error(response.error || 'Failed to update phone');
      }
    } catch (err) {
      console.error('Failed to update phone:', err);
      toast.error('Failed to update phone');
    } finally {
      setIsSavingPhone(false);
    }
  };

  // T2-2: Save commission rates
  const handleSaveCommission = async () => {
    if (!agentDetails?.id) return;
    setIsSavingCommission(true);
    try {
      const equipRate = commissionEquipInput
        ? Number.parseFloat(commissionEquipInput) / 100
        : undefined;
      const subsRate = commissionSubsInput
        ? Number.parseFloat(commissionSubsInput) / 100
        : undefined;
      const response = await adminApiRef.current.updateAgent(agentDetails.id, {
        commission_rate_equipment: equipRate,
        commission_rate_subs: subsRate,
      });
      if (response.success) {
        setAgentDetails((prev) =>
          prev
            ? {
                ...prev,
                commission_rate_equipment: equipRate ?? prev.commission_rate_equipment,
                commission_rate_subs: subsRate ?? prev.commission_rate_subs,
              }
            : null
        );
        setEditingCommission(false);
        toast.success('Commission rates updated');
      } else {
        toast.error(response.error || 'Failed to update commission rates');
      }
    } catch (err) {
      console.error('Failed to update commission rates:', err);
      toast.error('Failed to update commission rates');
    } finally {
      setIsSavingCommission(false);
    }
  };

  const handleAddProfile = async () => {
    if (!selectedUser) return;

    if (addProfileType === 'hq_admin' && hqAdminConfirmText !== 'HQ ADMIN') {
      toast.error('You must type "HQ ADMIN" to confirm this role assignment');
      return;
    }
    if (addProfileType === 'agent' && !addProfileTerritoryName.trim()) {
      toast.error('Territory name is required for agent profile');
      return;
    }
    if (addProfileType === 'affiliate_member' && !addProfileAffiliateId) {
      toast.error('Please select a gym');
      return;
    }

    setIsAddingProfile(true);
    try {
      const response = await adminApiRef.current.addUserProfile(selectedUser.id, {
        profile_type: addProfileType,
        territory_name: addProfileType === 'agent' ? addProfileTerritoryName.trim() : undefined,
        affiliate_id: addProfileType === 'affiliate_member' ? addProfileAffiliateId : undefined,
        member_role: addProfileType === 'affiliate_member' ? addProfileMemberRole : undefined,
      });
      if (response.success) {
        toast.success(response.data?.message || 'Profile added');
        setShowAddProfileModal(false);
        setAddProfileType('');
        setHqAdminConfirmText('');
        setAddProfileTerritoryName('');
        setAddProfileAffiliateId('');
        setAddProfileMemberRole('member');
        loadUserProfiles(selectedUser.id);
        fetchUsers();
      } else {
        toast.error(response.error || 'Failed to add profile');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add profile';
      toast.error(message);
    } finally {
      setIsAddingProfile(false);
    }
  };

  const handleRemoveProfile = async (profileId: string) => {
    if (!selectedUser) return;

    setRemovingProfileId(profileId);
    try {
      const response = await adminApiRef.current.removeUserProfile(selectedUser.id, profileId);
      if (response.success) {
        toast.success('Profile removed');
        loadUserProfiles(selectedUser.id);
        fetchUsers();
      } else {
        toast.error(response.error || 'Failed to remove profile');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to remove profile';
      toast.error(message);
    } finally {
      setRemovingProfileId(null);
    }
  };

  const handleReactivateProfile = async (profileId: string) => {
    if (!selectedUser) return;

    setRemovingProfileId(profileId);
    try {
      const response = await adminApiRef.current.reactivateUserProfile(selectedUser.id, profileId);
      if (response.success) {
        toast.success('Profile reactivated');
        loadUserProfiles(selectedUser.id);
        fetchUsers();
      } else {
        toast.error(response.error || 'Failed to reactivate profile');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to reactivate profile';
      toast.error(message);
    } finally {
      setRemovingProfileId(null);
    }
  };

  const handleSetPrimaryProfile = async (profileId: string) => {
    if (!selectedUser) return;

    setSettingPrimaryId(profileId);
    try {
      const response = await adminApiRef.current.setUserPrimaryProfile(selectedUser.id, profileId);
      if (response.success) {
        toast.success('Primary profile updated');
        loadUserProfiles(selectedUser.id);
      } else {
        toast.error(response.error || 'Failed to set primary profile');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to set primary profile';
      toast.error(message);
    } finally {
      setSettingPrimaryId(null);
    }
  };

  const handleSuspendUser = async (user: ApiUser) => {
    try {
      await adminApiRef.current.suspendUser(user.id);
      toast.success(`${user.name} has been suspended`);
      fetchUsers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to suspend user';
      toast.error(message);
    }
  };

  const handleActivateUser = async (user: ApiUser) => {
    try {
      await adminApiRef.current.activateUser(user.id);
      toast.success(`${user.name} has been activated`);
      fetchUsers();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to activate user';
      toast.error(message);
    }
  };

  const handleDeleteUser = (user: ApiUser) => {
    setSelectedUser(user);
    setDeleteConfirmText('');
    setShowDeleteModal(true);
  };

  const confirmDeleteUser = async () => {
    if (!selectedUser || deleteConfirmText !== 'DELETE') {
      toast.error('Please type DELETE to confirm');
      return;
    }

    setIsDeleting(true);
    try {
      const response = await adminApiRef.current.deleteUser(selectedUser.id);
      if (response.success) {
        toast.success(`${selectedUser.name} has been deleted.`);
        setShowDeleteModal(false);
        setSelectedUser(null);
        setDeleteConfirmText('');
        fetchUsers();
      } else {
        // BUG-MAY30-001 (delete) diagnostics: surface the API `detail` (real
        // error) on-screen so the root cause is visible without DevTools.
        const detail = (response as { detail?: string }).detail;
        toast.error(
          detail
            ? `${response.error || 'Failed to delete user'} — ${detail}`
            : response.error || 'Failed to delete user'
        );
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete user';
      toast.error(message);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleResendInvite = async (user: ApiUser) => {
    setResendingInviteFor(user.id);
    try {
      await adminApiRef.current.resendInvite(user.id);
      toast.success(`Invitation sent to ${user.email}`);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send invitation';
      toast.error(message);
    } finally {
      setResendingInviteFor(null);
    }
  };

  const handleRequestPasswordReset = async (user: ApiUser) => {
    setRequestingPasswordResetFor(user.id);
    try {
      await adminApiRef.current.requestPasswordReset(user.id);
      // BUG-MAR30-006: No email is sent — Clerk handles password resets via login page
      toast.success(
        `Tell ${user.name || user.email} to go to the login page and click "Forgot password?" to reset their password via Clerk.`,
        { duration: 10000 }
      );
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to process password reset request';
      toast.error(message);
    } finally {
      setRequestingPasswordResetFor(null);
    }
  };

  const handleBulkUpload = async () => {
    const emails = bulkEmails
      .split('\n')
      .map((line) => line.trim())
      .filter((line) => line?.includes('@'));

    if (emails.length === 0) {
      toast.error('No valid emails found. Enter one email per line.');
      return;
    }

    if (['affiliate_coach'].includes(bulkRole) && !bulkAffiliateId) {
      toast.error('Please select a gym for coaches');
      return;
    }

    setIsBulkUploading(true);
    setBulkResults(null);
    try {
      const response = await adminApiRef.current.bulkUploadUsers({
        users: emails.map((email) => ({
          email,
          role: bulkRole,
          affiliate_id: bulkAffiliateId || undefined,
        })),
      });
      if (response.success && response.data) {
        setBulkResults(response.data);
        toast.success(response.message || `${response.data.summary.created} users created`);
        fetchUsers();
      } else {
        toast.error(response.error || 'Bulk upload failed');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Bulk upload failed');
    } finally {
      setIsBulkUploading(false);
    }
  };

  // Use counts from API (stable totals regardless of filter)
  const userCounts = totalCounts;

  // Apply org filter client-side (filter D1 users by Clerk org membership)
  const filteredUsers = useMemo(() => {
    if (!orgFilter) return users;
    const memberIds = orgMembers.get(orgFilter) || [];
    if (memberIds.length === 0) return users; // Still loading or no members
    return users.filter((user) => memberIds.includes(user.id));
  }, [users, orgFilter, orgMembers]);

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="mb-8 flex flex-col items-start gap-4 sm:flex-row sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gradient-gold">User Management</h1>
          <p className="mt-1 text-muted-foreground">
            View and manage system users across all roles.
          </p>
        </div>
        <div className="flex w-full gap-2 sm:w-auto">
          <Button
            variant="outline"
            className="flex-1 gap-2 sm:flex-none"
            onClick={() => {
              setBulkEmails('');
              setBulkRole('member');
              setBulkAffiliateId('');
              setBulkResults(null);
              setShowBulkModal(true);
            }}
          >
            <Users className="h-4 w-4" />
            Bulk Upload
          </Button>
          <Button className="flex-1 gap-2 sm:flex-none" onClick={handleAddUser}>
            <Plus className="h-4 w-4" />
            Add User
          </Button>
        </div>
      </div>

      <UserStats
        roleFilter={roleFilter}
        handleRoleFilterChange={handleRoleFilterChange}
        userCounts={userCounts}
      />

      <UserFilters
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        roleFilter={roleFilter}
        handleRoleFilterChange={handleRoleFilterChange}
        affiliates={affiliates}
        gymFilter={gymFilter}
        setGymFilter={setGymFilter}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        organizations={organizations}
        orgFilter={orgFilter}
        setOrgFilter={setOrgFilter}
        loadingOrgMembers={loadingOrgMembers}
      />

      <UsersTable
        error={error}
        fetchUsers={fetchUsers}
        filteredUsers={filteredUsers}
        users={users}
        orgFilter={orgFilter}
        isLoading={isLoading}
        searchQuery={searchQuery}
        roleFilter={roleFilter}
        loadingOrgMembers={loadingOrgMembers}
        userOrgMap={userOrgMap}
        formatDate={formatDate}
        handleViewUser={handleViewUser}
        handleResendInvite={handleResendInvite}
        resendingInviteFor={resendingInviteFor}
        handleRequestPasswordReset={handleRequestPasswordReset}
        requestingPasswordResetFor={requestingPasswordResetFor}
        handleSuspendUser={handleSuspendUser}
        handleActivateUser={handleActivateUser}
        handleDeleteUser={handleDeleteUser}
        hasMore={hasMore}
        nextCursor={nextCursor}
      />

      <BulkUploadDialog
        showBulkModal={showBulkModal}
        setShowBulkModal={setShowBulkModal}
        bulkRole={bulkRole}
        setBulkRole={setBulkRole}
        affiliates={affiliates}
        bulkAffiliateId={bulkAffiliateId}
        setBulkAffiliateId={setBulkAffiliateId}
        bulkEmails={bulkEmails}
        setBulkEmails={setBulkEmails}
        bulkResults={bulkResults}
        handleBulkUpload={handleBulkUpload}
        isBulkUploading={isBulkUploading}
      />

      <InviteUserDialog
        showAddModal={showAddModal}
        setShowAddModal={setShowAddModal}
        inviteEmail={inviteEmail}
        setInviteEmail={setInviteEmail}
        inviteName={inviteName}
        setInviteName={setInviteName}
        inviteRole={inviteRole}
        setInviteRole={setInviteRole}
        inviteAffiliateId={inviteAffiliateId}
        setInviteAffiliateId={setInviteAffiliateId}
        inviteAffiliateName={inviteAffiliateName}
        setInviteAffiliateName={setInviteAffiliateName}
        affiliates={affiliates}
        handleInviteUser={handleInviteUser}
        isInviting={isInviting}
      />

      <UserDetailsDialog
        showViewModal={showViewModal}
        selectedUser={selectedUser}
        setShowViewModal={setShowViewModal}
        formatDate={formatDate}
        editingPhone={editingPhone}
        phoneInput={phoneInput}
        setPhoneInput={setPhoneInput}
        handleSavePhone={handleSavePhone}
        isSavingPhone={isSavingPhone}
        setEditingPhone={setEditingPhone}
        userPhone={userPhone}
        agentDetails={agentDetails}
        editingCommission={editingCommission}
        setCommissionEquipInput={setCommissionEquipInput}
        setCommissionSubsInput={setCommissionSubsInput}
        setEditingCommission={setEditingCommission}
        commissionEquipInput={commissionEquipInput}
        commissionSubsInput={commissionSubsInput}
        handleSaveCommission={handleSaveCommission}
        isSavingCommission={isSavingCommission}
        paymentDetails={paymentDetails}
        setAddProfileType={setAddProfileType}
        setHqAdminConfirmText={setHqAdminConfirmText}
        setAddProfileTerritoryName={setAddProfileTerritoryName}
        setAddProfileAffiliateId={setAddProfileAffiliateId}
        setAddProfileMemberRole={setAddProfileMemberRole}
        setShowAddProfileModal={setShowAddProfileModal}
        isLoadingProfiles={isLoadingProfiles}
        userProfiles={userProfiles}
        handleReactivateProfile={handleReactivateProfile}
        removingProfileId={removingProfileId}
        handleSetPrimaryProfile={handleSetPrimaryProfile}
        settingPrimaryId={settingPrimaryId}
        handleRemoveProfile={handleRemoveProfile}
      />

      <AddProfileDialog
        showAddProfileModal={showAddProfileModal}
        selectedUser={selectedUser}
        setShowAddProfileModal={setShowAddProfileModal}
        addProfileType={addProfileType}
        setAddProfileType={setAddProfileType}
        hqAdminConfirmText={hqAdminConfirmText}
        setHqAdminConfirmText={setHqAdminConfirmText}
        userProfiles={userProfiles}
        addProfileTerritoryName={addProfileTerritoryName}
        setAddProfileTerritoryName={setAddProfileTerritoryName}
        affiliates={affiliates}
        addProfileAffiliateId={addProfileAffiliateId}
        setAddProfileAffiliateId={setAddProfileAffiliateId}
        addProfileMemberRole={addProfileMemberRole}
        setAddProfileMemberRole={setAddProfileMemberRole}
        handleAddProfile={handleAddProfile}
        isAddingProfile={isAddingProfile}
      />

      <DeleteUserDialog
        showDeleteModal={showDeleteModal}
        selectedUser={selectedUser}
        setShowDeleteModal={setShowDeleteModal}
        deleteConfirmText={deleteConfirmText}
        setDeleteConfirmText={setDeleteConfirmText}
        confirmDeleteUser={confirmDeleteUser}
        isDeleting={isDeleting}
      />
    </DashboardLayout>
  );
}
